import get from 'lodash-es/get';
import set from 'lodash-es/set';
import plans from '../plans';
import {
  AccountTypeEnum,
  BILLING_CYCLE,
  flowTypes,
  freeTrialPrefixKey,
  planTypes,
  SALES_CHANNEL,
  TRAIL_CONDITION_TYPE,
} from '../constant';
import { preCheckForFreeTrial, preCheckForFreeTrialByCategory, preCheckForPaid } from '../api';
import { getPlanTypeByCategory } from './parse';
import { logger } from '../../../common/logger';

/**
 * @description can filter by caller send props
 * @param planList {ProductFreeTrialEntryResponse[]}
 * @param options {PreCheckProductEntryParams}
 */
export const getCurrentEntryDataByOptions = (planList = [], options = {}) => {
  const { capacity, period, entryCategoryCode } = options;

  // has category param, first check category
  if (entryCategoryCode) {
    const categoryList = planList?.filter((p) => {
      return p.categoryCode === entryCategoryCode
        && (capacity ? capacity === p?.capacity : true)
        && (period ? BILLING_CYCLE[p.billingCycle] === period : true)
    });

    const result = get(categoryList, [0]);
    logger.productEntry(`fromIn:category-${entryCategoryCode}, capacity-${capacity}, period-${period}`, result);
    return result;
  }

  // has capacity param, second check capacity
  if (capacity) {
    const capacityPlanList = planList?.filter((p) => {
      // have period, need combine capacity and period params
      return p.capacity === capacity
        && (period ? BILLING_CYCLE[p.billingCycle] === period : true);
    });

    const result = get(capacityPlanList, [0]);
    logger.productEntry(`fromIn:capacity-${capacity}, period-${period}`, result);
    return result;
  }

  // has period param, third check period
  if (period) {
    const cyclePlanList = planList?.filter((p) => BILLING_CYCLE[p.billingCycle] === period);
    const result = get(cyclePlanList, [0]);
    logger.productEntry(`fromIn:period-${period}`, result);
    return result;
  }

  // last get first item
  const result = get(planList, [0]);
  logger.productEntry('fromIn:firstData', result);
  return result;
};

/**
 *
 * @param entryType {string}
 * @param freeTrial {boolean}
 * @param planType {PlanType}
 * @return {string}
 */
const generateCacheKey = ({ entryType, freeTrial, planType, categoryCode, salesChannel }) => {
  return `${freeTrial ? freeTrialPrefixKey : ''}${entryType ?? ''}${planType ?? ''}${categoryCode ?? ''}${salesChannel ?? ''}`;
};

/**
 *
 * @return {ProductEntryResultExBean}
 */
export const getDefaultProductEntryData = () => {
  return {
    planType: '', // current purchase planType
    eligible: false, // control purchase product entry is displayed (for regular or free trial plan)
    link: '',
    buttonTitle: '',
    disabled: false,
    flowType: '',
    trialCondition: '',
    currentUserHasPermission: false,
    remainTrialDays: null,
  };
};

/**
 * @param p {GenerateProductEntryParams}
 * @return string
 */
const getFlowType = (params = {}) => {
  /**@type {ProductEntryDataBean} */
  const entryData = params?.entryData;
  const trialCondition = entryData?.trialCondition;
  const accountType = entryData?.accountType;

  const p = [trialCondition];

  if (TRAIL_CONDITION_TYPE.isEligible(...p)) {
    const isNNOrFree = [
      AccountTypeEnum.ACCOUNT_UNKNOWN,
      AccountTypeEnum.ACCOUNT_FREE_WITH_CC,
      AccountTypeEnum.ACCOUNT_FREE_WITHOUT_CC,
    ].includes(accountType);

    if (isNNOrFree) {
      return flowTypes.buyTrial;
    }

    return flowTypes.addTrial;
  }

  if (TRAIL_CONDITION_TYPE.isTrialing(...p)) {
    return flowTypes.trial2paid;
  }

  if (TRAIL_CONDITION_TYPE.isTrialExpired(...p)) {
    return flowTypes.add2cart;
  }

  return '';
};

/**
 * @description need planType params to find plan's flow constructor
 * @description Generate the purchase link and check status information of the corresponding plan based on the back-end verification results.
 * @param p {GenerateProductEntryParams}
 * @return {ProductEntryResultExBean}
 */
export const generateProductEntry = (p = {}) => {
  const { entryData, planType, basePlan, period } = p;

  // todo need consider generate purchase paid button flow
  const flowType = getFlowType(p);
  const planTypeCamelName = planTypes[planType];
  const FlowPlanConstructor = get(plans, [planTypeCamelName, flowType]);
  const result = { ...entryData, planType, flowType, eligible: false, link: '', disabled: true };

  if (flowType && FlowPlanConstructor) {
    const instance = new FlowPlanConstructor(entryData, { flowType, basePlan, period });

    return {
      ...result,
      ...instance.getOptions(),
      link: instance.getLink(),
      pageQueryConfig: instance.getCurrentParams?.(),
      eligible: true,
    };
  }

  logger.productEntryError('flowType', `flowType: ${flowType} can not find in FlowPlanConstructor(${planTypeCamelName})!!`);

  return result;
};

const apiPromisesCache = {};

/**
 *
 * @param options {PreCheckProductEntryParams}
 */
const getFreeTrialPlanList = (options) => {
  const { entryType, salesChannel, categoryCode } = options || {};

  if (salesChannel === SALES_CHANNEL.direct) {
    return preCheckForFreeTrialByCategory({ categoryCode, online: false });
  }

  return preCheckForFreeTrial({ entryType });
}

/**
 * @description For the caller to check in advance whether the current account allows purchases
 * @param options {PreCheckProductEntryParams}
 * @return {Promise<unknown>}
 */
const preCheckApiWith$cache = async (options) => {
  const { freeTrial, entryType, useCache = true, categoryCode, salesChannel } = options || {};
  const cacheKey = generateCacheKey({ entryType, freeTrial, categoryCode, salesChannel });

  // Check if there is already a request in progress
  if (cacheKey && useCache && apiPromisesCache[cacheKey]) {
    logger.productEntry('fromCache', `cacheKey: ${cacheKey}`);
    return apiPromisesCache[cacheKey]?.promise;
  }

  const promise = new Promise(async (resolve) => {
    try {
      const { result } = await (freeTrial ? getFreeTrialPlanList(options) : preCheckForPaid());

      // Set the Promise's status to Completed
      resolve(result);
    } catch (error) {
      logger.productEntryError('preCheckApiWith$cache', error);
      resolve([]);
    } finally {
      // Reset loading to false, indicating that the request has been processed
      cacheKey && set(apiPromisesCache, [cacheKey, 'loading'], false);
    }
  });

  // Store Promise and loading state in global state
  cacheKey && (apiPromisesCache[cacheKey] = { loading: true, promise });

  return promise;
};

export const syncPreCheckForProductEntry = (options = {}) => {
  try {
    const entryOptions = options?.entryOptions;

    // Incoming metadata from outside the caller
    logger.productEntry('customPropsIn:entryOptions', entryOptions);
    const planType = getPlanTypeByCategory(entryOptions?.categoryCode);
    if (!planType) {
      logger.productEntry('customPropsIn:entryOptions', `can not find respond planType with service category Code ${entryOptions?.categoryCode}`);
      return {};
    }

    const result = generateProductEntry({
      planType,
      entryData: entryOptions,
      ...options,
    });

    logger.productEntry('initExternalOut:result', result);
    return result;
  } catch (e) {
    logger.productEntryError("syncPreCheckForProductEntry", e);
    return {};
  }
}

/**
 * @description For the caller to check in advance whether the current account allows purchases
 * @param options {PreCheckProductEntryParams}
 * @return {Promise<unknown>}
 */
export const preCheckForProductEntry = async (options) => {
  const { freeTrial, entryType, ...restOptions } = options || {};

  // Incoming metadata from outside the caller
  if (options?.entryOptions) {
    const result = syncPreCheckForProductEntry(options);
    return Promise.resolve(result);
  }

  // need call api with inner product entry component
  // purchase free trial plan flow need have entryType parameter
  if (freeTrial && !entryType) {
    throw new Error('should entryType parameters for free trial plans');
  }

  try {
    const result = await preCheckApiWith$cache(options);

    const entryData = getCurrentEntryDataByOptions(result, options);
    if (!entryData) return Promise.resolve({});
    const planType = getPlanTypeByCategory(entryData?.categoryCode);
    const params = { planType, entryData, freeTrial, ...restOptions };
    const productEntryResult = generateProductEntry(params);
    logger.productEntry("initInternalOut:result", productEntryResult);
    return Promise.resolve(productEntryResult);
  } catch (e) {
    logger.productEntryError("preCheckForProductEntry", e);
    return Promise.resolve({});
  }
};

export const clearProductEntryCacheByPlanType = (options) => {
  const key = generateCacheKey(options);

  if (get(apiPromisesCache, [key])) {
    delete apiPromisesCache[key];
  }
};
